import {
  NodoDto,
  getDispositivoHistoryData,
  ProprietaMisurataDataPeriod,
  ProprietaMisurataDto,
  ProprietaMisurataValueDto,
} from "@/api";
import Button from "@/elements/Button";
import { withOpeningReset } from "@/utils/withOpeningReset";
import { Autocomplete, InputLabel, TextField, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { DateField } from "@mui/x-date-pickers";
import { orderBy } from "lodash";
import moment from "moment";
import * as Papa from "papaparse";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SmallLoading } from "../Loading";
import { toast } from "react-toastify";

const availablePeriods = Object.values(ProprietaMisurataDataPeriod);

function DispositivoCSVDialog({
  open = false,
  onClose,
  dispositivo,
}: {
  open?: boolean;
  onClose?: (dispositivo: NodoDto | null) => void;
  dispositivo?: NodoDto;
}) {
  const { t } = useTranslation();

  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [misura, setMisura] = useState<ProprietaMisurataDto | null>(null);
  const [period, setPeriod] = useState<ProprietaMisurataDataPeriod | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const isValid = useMemo(() => {
    return !!from && !!to && !!misura && !!period;
  }, [from, to, misura, period]);

  const availableMisure = useMemo(() => {
    const proprieta = dispositivo?.dispositivoProprietaMisurate || [];
    return orderBy(proprieta, (pm) => pm.type.order);
  }, [dispositivo]);

  const handleClose = () => {
    onClose?.(null);
  };

  const handleSubmit = async (onSubmit: any) => {
    try {
      setIsLoading(true);

      const dispositivoId = dispositivo?.id;
      if (!dispositivoId) {
        throw new Error(t("dispositivoCsvDialog.errori.dispositivoNonTrovato"));
      }
      if (misura === null) {
        throw new Error(t("dispositivoCsvDialog.errori.misuraNonSelezionata"));
      }
      if (period === null) {
        throw new Error(t("dispositivoCsvDialog.errori.periodoNonSelezionato"));
      }
      if (!from) {
        throw new Error(t("dispositivoCsvDialog.errori.dataInizioNonSelezionata"));
      }
      const dateFromString = moment(from).startOf("day").toISOString();

      if (!to) {
        throw new Error(t("dispositivoCsvDialog.errori.dataFineNonSelezionata"));
      }
      const dateToString = moment(to).endOf("day").toISOString();

      const data = await getDispositivoHistoryData(dispositivo?.id, misura.id, {
        _from: dateFromString,
        _to: dateToString,
        _period: period,
      });

      if (data.data.length > 0) {
        const title =
          dispositivo.dispositivoMatricola + "_" + misura.type?.label;
        downloadCSV(title, data.data);
        handleClose();
      } else {
        const msg = t("dispositivoCsvDialog.errori.nessunDato");
        setExportError(msg);
        toast.warning(msg);
      }
    } catch (error) {
      console.error(error);
      const msg = t("dispositivoCsvDialog.errori.generazioneCsv");
      setExportError(msg);
      toast.warning(msg);
    }
    setIsLoading(false);
  };

  const downloadCSV = (
    title: string,
    proprietaMisurate: ProprietaMisurataValueDto[]
  ) => {
    const csv = createCSV(proprietaMisurate);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", title);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const createCSV = (proprietaMisurate: ProprietaMisurataValueDto[]) => {
    const csvRows: Record<string, string | null>[] = [];

    proprietaMisurate.forEach((proprietaMisurata) => {
      const row = {
        [t("dispositivoCsvDialog.csv.data")]: moment(
          proprietaMisurata.timestamp
        ).format("DD/MM/YYYY HH:mm:ss"),
        [t("dispositivoCsvDialog.csv.valore")]:
          proprietaMisurata.value?.toString()?.replace(".", ",") ?? null,
        //proprietaMisurata.dangerMin,
        //proprietaMisurata.dangerMax,
      };
      csvRows.push(row);
    });

    return Papa.unparse(csvRows, { delimiter: ";" });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("dispositivoCsvDialog.titolo")}</DialogTitle>
      <DialogContent>
        <InputLabel htmlFor="csv_misura">
          {t("dispositivoCsvDialog.campi.misura")}
        </InputLabel>
        <Autocomplete<ProprietaMisurataDto>
          onChange={(e, value) => setMisura(value)}
          options={availableMisure}
          getOptionLabel={(misura) => misura.type.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={misura}
          renderInput={(params) => <TextField {...params} value={misura} />}
          size="medium"
        />

        <InputLabel htmlFor="csv_period">
          {t("dispositivoCsvDialog.campi.cardinalita")}
        </InputLabel>
        <Autocomplete<string>
          onChange={(e, value) =>
            setPeriod(value as ProprietaMisurataDataPeriod)
          }
          options={availablePeriods}
          getOptionLabel={(period) => period}
          isOptionEqualToValue={(option, value) => option === value}
          value={period}
          renderInput={(params) => <TextField {...params} value={period} />}
          size="medium"
        />

        <InputLabel htmlFor="csv_da">
          {t("dispositivoCsvDialog.campi.da")}
        </InputLabel>
        <DateField
          value={from}
          onChange={(date) => {
            setFrom(date);
          }}
        />

        <InputLabel htmlFor="csv_a">
          {t("dispositivoCsvDialog.campi.a")}
        </InputLabel>
        <DateField value={to} onChange={(date) => setTo(date)} />

        {exportError && (
          <Typography variant="subtitle2" color="error">
            {exportError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isLoading}
          onClick={handleSubmit}
        >
          {t("azioni.scarica")}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(DispositivoCSVDialog);
